import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, UploadCloud, X, GripVertical, Loader2, Image as ImageIcon, Eye, EyeOff } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { pastWorkApi, eventsApi, fileUrl } from "@/lib/api";

const CATEGORIES = ["Wedding", "Reunion", "Sports Event", "Live Show", "Birthday", "Anniversary", "Corporate Event", "Celebration", "Other"];
const inputCls = "w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";

const EMPTY = {
  title: "", description: "", event_date: "", location: "", category: "Wedding",
  client_name: "", testimonial: "", cover_image: "", gallery: [], visible: true, order: 0,
};

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
  </label>
);

export default function PastWorkEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [galBusy, setGalBusy] = useState(false);
  const dragIdx = useRef(null);

  const load = useCallback(async () => {
    if (!editing) return;
    try {
      const all = await pastWorkApi.listAll();
      const found = all.find((x) => x.id === id);
      if (!found) throw new Error();
      setForm({ ...EMPTY, ...found });
    } catch {
      toast.error("Past work not found");
      navigate("/past-work/manage");
    } finally {
      setLoading(false);
    }
  }, [editing, id, navigate]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Cover must be an image"); return; }
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await eventsApi.uploadMedia(fd);
      set("cover_image", fileUrl(res.url));
      toast.success("Cover updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cover upload failed");
    } finally {
      setCoverBusy(false);
    }
  };

  const uploadGallery = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setGalBusy(true);
    try {
      const uploaded = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await eventsApi.uploadMedia(fd);
        uploaded.push({ id: res.id, url: res.url, type: res.type, filename: res.filename });
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...uploaded] }));
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setGalBusy(false);
    }
  };

  const removeMedia = (mid) => set("gallery", (form.gallery || []).filter((m) => m.id !== mid));
  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const arr = [...(form.gallery || [])];
    const [moved] = arr.splice(from, 1);
    arr.splice(i, 0, moved);
    set("gallery", arr);
    dragIdx.current = null;
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Please add an event title"); return; }
    setSaving(true);
    try {
      const { id: _i, created_at: _c, ...payload } = form;
      if (editing) await pastWorkApi.update(id, payload);
      else await pastWorkApi.create(payload);
      toast.success(editing ? "Past work updated" : "Past work added");
      navigate("/past-work/manage");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-background"><Navbar /><div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-4xl px-5 py-8 sm:px-8">
        <button onClick={() => navigate("/past-work/manage")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="pw-back"><ArrowLeft className="h-4 w-4" /> Back to portfolio</button>

        <div className="flex items-center justify-between">
          <div>
            <p className="eyebrow text-gold">Portfolio</p>
            <h1 className="mt-1 font-serif text-3xl font-semibold">{editing ? "Edit Past Event" : "Add Past Event"}</h1>
          </div>
          <button onClick={save} disabled={saving} data-testid="pw-save-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>

        <div className="mt-8 space-y-6 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label="Event title"><input data-testid="pw-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Wedding Celebration – Jaipur" /></Field>
            </div>
            <Field label="Category">
              <select data-testid="pw-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Event date (optional)" hint="Free text — e.g. December 2025 or 10–12 Dec 2026"><input data-testid="pw-date" className={inputCls} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} placeholder="December 2025" /></Field>
            <Field label="Location / venue (optional)"><input data-testid="pw-location" className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="The Gwalior Fort, Gwalior" /></Field>
            <Field label="Client name (optional)"><input data-testid="pw-client" className={inputCls} value={form.client_name} onChange={(e) => set("client_name", e.target.value)} placeholder="The Singhvi Family" /></Field>
            <div className="sm:col-span-2">
              <Field label="About the event"><textarea data-testid="pw-description" rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="A short description of the event you delivered…" /></Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Client testimonial / feedback"><textarea data-testid="pw-testimonial" rows={3} className={inputCls} value={form.testimonial} onChange={(e) => set("testimonial", e.target.value)} placeholder="“Godinga handled every detail perfectly…”" /></Field>
            </div>
          </div>

          {/* Cover */}
          <div>
            <p className="mb-1.5 text-sm font-medium">Cover image (optional)</p>
            <p className="mb-2 text-xs text-muted-foreground">Used as the card thumbnail. If left empty, the first gallery photo is used.</p>
            <div className="flex items-center gap-4">
              <div className="h-24 w-36 shrink-0 overflow-hidden rounded-xl border border-border bg-secondary">
                {form.cover_image ? <img src={form.cover_image} alt="cover" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><ImageIcon className="h-6 w-6 text-muted-foreground" /></div>}
              </div>
              <div className="flex gap-2">
                <label data-testid="pw-cover-upload" className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary">
                  {coverBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload cover
                  <input type="file" accept="image/*" className="hidden" disabled={coverBusy} onChange={(e) => uploadCover(e.target.files?.[0])} />
                </label>
                {form.cover_image && <button onClick={() => set("cover_image", "")} className="rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/10">Remove</button>}
              </div>
            </div>
          </div>

          {/* Gallery */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-medium">Photo gallery</p>
              <label data-testid="pw-gallery-upload" className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
                {galBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Add photos
                <input type="file" accept="image/*,video/*" multiple className="hidden" disabled={galBusy} onChange={(e) => uploadGallery(e.target.files)} />
              </label>
            </div>
            {(form.gallery || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No photos yet — upload multiple photos to build this event's gallery. Drag to reorder.</div>
            ) : (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {form.gallery.map((m, i) => (
                  <div
                    key={m.id || i}
                    draggable
                    onDragStart={() => (dragIdx.current = i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(i)}
                    data-testid={`pw-gallery-item-${i}`}
                    className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-secondary"
                  >
                    {m.type === "video" ? <video src={fileUrl(m.url)} className="h-full w-full object-cover" muted /> : <img src={fileUrl(m.url)} alt={m.filename || "photo"} className="h-full w-full object-cover" />}
                    <span className="absolute left-1.5 top-1.5 rounded-md bg-black/50 p-1 text-white"><GripVertical className="h-3.5 w-3.5" /></span>
                    <button onClick={() => removeMedia(m.id)} data-testid={`pw-gallery-remove-${i}`} className="absolute right-1.5 top-1.5 rounded-md bg-black/50 p-1 text-white opacity-0 transition-opacity hover:bg-destructive group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Visibility */}
          <label className="flex cursor-pointer items-center justify-between rounded-xl border border-border px-4 py-3">
            <span className="inline-flex items-center gap-2 text-sm font-medium">
              {form.visible ? <Eye className="h-4 w-4 text-gold" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
              Show this event on the public Past Work page
            </span>
            <input type="checkbox" checked={form.visible} onChange={(e) => set("visible", e.target.checked)} className="h-4 w-4 accent-[hsl(var(--gold))]" data-testid="pw-visible" />
          </label>
        </div>
      </div>
    </div>
  );
}
